import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { reduxForm } from 'redux-form';

import FlagEnabledContainer from 'components/FlagEnabled/FlagEnabledContainer';
import ORTypography from 'components/ORTypography/ORTypography';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import FlavorSelectorContainer from './FlavorSelector/FlavorSelectorContainer';
import AddonSelector from './AddonSelector/AddonSelector';
import PlanSelectorContainer from './PlanSelector/PlanSelectorContainer';

const propTypes = {
  availablePlans: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  availableFlavors: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  availableAddons: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  selectedPlan: PropTypes.shape({ id: PropTypes.string }).isRequired,
  selectedFlavor: PropTypes.shape({ id: PropTypes.string }).isRequired,
  selectedService: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
  nodeInfoString: PropTypes.string.isRequired,
  addonInfoString: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => ({
  stepSection: {
    marginBottom: theme.spacing(3),
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    fontSize: '.8571rem',
  },
  flavorSelectorContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  flavorSelector: {
    maxWidth: 210,
    margin: `0 ${theme.spacing(1)}px`,

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
      marginBottom: theme.spacing(1),
    },
  },
  addonSelector: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  planSelector: {
    width: '100%',
  },
  planInfo: {
    color: theme.customColors.orange,
    fontSize: '.85714rem',
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5),
  },
}));

export function UnstyledStepTwo({
  availableFlavors,
  availableAddons,
  availablePlans,
  selectedPlan,
  selectedFlavor,
  selectedService,
  trackButtonClick,
  change,
  nodeInfoString,
  addonInfoString,
}) {
  const [mongoServiceType, setMongoServiceType] = React.useState('replicaset');
  const classes = useStyles();

  // Select the first available flavor by default
  React.useEffect(() => {
    if (availableFlavors.length > 0) {
      const flavor = availableFlavors.find((af) => af.id === selectedFlavor.id);
      if (!flavor) {
        change('flavor', availableFlavors[0].id);
      }
    }
  }, [availableFlavors, change, selectedFlavor]);

  // Select the first available plan by default
  React.useEffect(() => {
    if (availablePlans.length > 0) {
      const plan = availablePlans.find((ap) => ap.id === selectedPlan.id);
      if (!plan) {
        change('plan', availablePlans[0].id);
      }
    }
  }, [availablePlans, change, selectedPlan]);

  const flavorSelectors = availableFlavors.map((flavor) => (
    <FlavorSelectorContainer
      className={classes.flavorSelector}
      key={flavor.id}
      flavorId={flavor.id}
      title={flavor.name}
      description={flavor.description}
      isSelected={selectedFlavor.id === flavor.id}
      onClick={() => change('flavor', flavor.id)}
    />
  ));

  const addonSelectors = availableAddons.map((addon) => (
    <SegmentClickTracker
      key={addon.id}
      buttonName={selectedService.concat(' ', addon.displayName, ' Add On')}
    >
      <AddonSelector
        className={classes.addonSelector}
        key={addon.id}
        addonId={addon.id}
        displayName={addon.displayName}
        name={addon.name}
        description={addon.description}
        addonValue={addon.value}
        trackButtonClick={trackButtonClick}
        selectedService={selectedService}
      />
    </SegmentClickTracker>
  ));

  const isMongo = selectedService === 'MongoDB';
  let showAddons = false;
  if (!isMongo || mongoServiceType === 'sharded') {
    showAddons = true;
  }

  return (
    <form>
      <div className={classes.stepSection}>
        <ORTypography className={classes.sectionLabel}>Pick Your Flavor:</ORTypography>
        <div className={classes.flavorSelectorContainer}>{flavorSelectors}</div>
        <FlagEnabledContainer flagKey="mongos">
          {isMongo && (
            <Grid container spacing={2}>
              <Grid item xs>
                <FlavorSelectorContainer
                  title="Replicaset"
                  description="This is the replicaset type"
                  isSelected={mongoServiceType === 'replicaset'}
                  onClick={() => {
                    setMongoServiceType('replicaset');
                    change('addons', undefined);
                  }}
                />
              </Grid>
              <Grid item xs>
                <FlavorSelectorContainer
                  title="Sharded"
                  description="This is the sharded type"
                  isSelected={mongoServiceType === 'sharded'}
                  onClick={() => setMongoServiceType('sharded')}
                />
              </Grid>
            </Grid>
          )}
        </FlagEnabledContainer>
      </div>

      <div className={classes.stepSection}>
        <ORTypography className={classes.sectionLabel}>Customize Your Instance:</ORTypography>
        {showAddons && <div>{addonSelectors}</div>}
      </div>

      <div className={classes.stepSection}>
        <PlanSelectorContainer className={classes.planSelector} name="plan" />
        {isMongo && <p className={classes.planInfo}>Plan includes two replicas.</p>}
        <p className={classes.planInfo}>
          {nodeInfoString}
          {addonInfoString}
        </p>
      </div>
    </form>
  );
}

UnstyledStepTwo.propTypes = propTypes;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledStepTwo);
